import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import useDesks from '@/hooks/useDesks';
import { Desk } from '@/shared/types/desk';

import { FilterCheckboxItem } from './FilterCheckboxItem';
import { deskSearchTerms } from './filterSearchTerms';
import { SearchableCheckboxList } from './SearchableCheckboxList';
import { FilterPanelProps } from './types';

/**
 * Renders a list of available environments (desks) with checkboxes for filtering
 */
export const EnvironmentsFilter = ({
  filterIndex,
  searchText = '',
  onSearchChange = () => {},
}: FilterPanelProps) => {
  const { t } = useTranslation();
  const { desks } = useDesks();
  const { control, getValues } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  const deskIds = useWatch({ control, name: `filters.${filterIndex}.deskIds` }) || [];

  const handleDeskToggle = (deskId: string, checked: boolean) => {
    const currentDeskIds = Array.isArray(deskIds) ? [...deskIds] : [];
    const newDeskIds = checked
      ? [...currentDeskIds, deskId]
      : currentDeskIds.filter((id: string) => id !== deskId);
    const currentFilter = getValues(`filters.${filterIndex}`) as { type?: string } | undefined;
    const fallbackType = currentFilter?.type ?? 'environments';
    const safeFilter =
      currentFilter && typeof currentFilter === 'object' ? currentFilter : { type: fallbackType };

    const nextFilter = {
      ...safeFilter,
      type: safeFilter.type ?? fallbackType,
      deskIds: newDeskIds,
    };

    update(filterIndex, nextFilter);
  };

  if (!desks || desks.length === 0) {
    return <FilterEmptyState text={t('analytics.filters.no_environments')} />;
  }

  return (
    <SearchableCheckboxList
      searchText={searchText}
      onSearchChange={onSearchChange}
      items={desks}
      getSearchTerms={(desk: Desk) => deskSearchTerms(desk)}
      placeholder={t('analytics.filters.search_environments')}
      renderItem={(desk: Desk) => (
        <FilterCheckboxItem
          key={desk.desk_id}
          id={`desk-${desk.desk_id}`}
          checked={deskIds.includes(desk.desk_id)}
          onCheckedChange={(checked) => handleDeskToggle(desk.desk_id, checked as boolean)}
        >
          {desk.name}
        </FilterCheckboxItem>
      )}
    />
  );
};
